type StringifyValueOptions = {
    /** When serializing a string, this string is added before and after the value */
    stringQuotationMark: string;
};

const defaultStringifyValueOptions: StringifyValueOptions = {
    stringQuotationMark: '',
};

const doubleQuoteStringifyValueOptions: StringifyValueOptions = {
    stringQuotationMark: '"',
};

/**
 * Stringify any value with as much information as possible (special handling for arrays, sets, maps and objects)
 *
 * @param value Value to stringify
 * @param options Options for stringification
 *
 * @returns String representing `value`
 */
export function stringifyValue(value: unknown, options: Partial<StringifyValueOptions> = {}): string {
    const stringQuotationMark = options.stringQuotationMark ?? defaultStringifyValueOptions.stringQuotationMark;

    switch (typeof value) {
        case 'boolean':
        case 'number':
        case 'undefined':
            return `${value}`;
        case 'bigint':
            return `${value}n`;
        case 'string':
            return `${stringQuotationMark}${value}${stringQuotationMark}`;
        case 'symbol':
            return `Symbol(${value.description ?? ''})`;
        case 'function':
            return `function ${value.name}()`;
        case 'object': {
            if (value === null) {
                return `${value}`;
            }

            if (Array.isArray(value)) {
                return stringifyArray(value);
            }

            if (value instanceof Set) {
                return stringifySet(value);
            }

            if (value instanceof Map) {
                return stringifyMap(value);
            }

            try {
                return JSON.stringify(value);
            } catch {
                // ignore
            }

            break;
        }
    }

    try {
        return String(value);
    } catch {
        return '';
    }
}

function stringifyArray(value: unknown[]): string {
    let result = '[';

    let isFirst = true;
    for (const val of value) {
        if (isFirst) {
            isFirst = false;
        } else {
            result += ', ';
        }

        result += stringifyValue(val, doubleQuoteStringifyValueOptions);
    }

    return result + ']';
}

function stringifySet(value: Set<unknown>): string {
    let result = 'Set(';

    let isFirst = true;
    for (const val of value.values()) {
        if (isFirst) {
            isFirst = false;
        } else {
            result += ', ';
        }

        result += stringifyValue(val, doubleQuoteStringifyValueOptions);
    }

    return result + ')';
}

function stringifyMap(value: Map<unknown, unknown>): string {
    let result = 'Map(';

    let isFirst = true;
    for (const [key, val] of value.entries()) {
        if (isFirst) {
            isFirst = false;
        } else {
            result += ', ';
        }

        const keyString = stringifyValue(key, doubleQuoteStringifyValueOptions);
        const valString = stringifyValue(val, doubleQuoteStringifyValueOptions);
        result += `${keyString} => ${valString}`;
    }

    return result + ')';
}
